import { escapeHtml } from './attributeParsing.js';
import { pdfLabelFor, renderLabeledValuePdfHtml } from './pdf.js';
export const ADDRESS_SUBFIELDS = ['country', 'line1', 'line2', 'city', 'state', 'zip'];
/** Standard checkout required set. line2 and state/province stay optional. */
export const REQUIRED_ADDRESS_SUBFIELDS = ['country', 'line1', 'city', 'zip'];
export const EMPTY_ADDRESS = { line1: '', line2: '', city: '', state: '', zip: '', country: '' };
/**
 * Parse a stored address value. Returns null for absent, non-JSON, or
 * non-object input; the guest UI can only produce well-formed JSON, so
 * anything unparseable is treated as "no value entered".
 */
export const parseAddressValue = (raw) => {
    if (typeof raw !== 'string' || !raw.trim())
        return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
        return null;
    const source = parsed;
    const result = { ...EMPTY_ADDRESS };
    for (const key of ADDRESS_SUBFIELDS) {
        const value = source[key];
        result[key] = typeof value === 'string' ? value.trim() : '';
    }
    return result;
};
export const serializeAddressValue = (value) => JSON.stringify(value);
export const isAddressEmpty = (value) => ADDRESS_SUBFIELDS.every((key) => !value[key]);
export const missingRequiredAddressSubfields = (value) => REQUIRED_ADDRESS_SUBFIELDS.filter((key) => !value[key]);
/** English display names, stored verbatim as the country value. */
export const COUNTRY_NAMES = [
    'United States',
    'Afghanistan',
    'Albania',
    'Algeria',
    'Andorra',
    'Angola',
    'Antigua and Barbuda',
    'Argentina',
    'Armenia',
    'Australia',
    'Austria',
    'Azerbaijan',
    'Bahamas',
    'Bahrain',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'Belgium',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Botswana',
    'Brazil',
    'Brunei',
    'Bulgaria',
    'Burkina Faso',
    'Burundi',
    'Cabo Verde',
    'Cambodia',
    'Cameroon',
    'Canada',
    'Central African Republic',
    'Chad',
    'Chile',
    'China',
    'Colombia',
    'Comoros',
    'Congo',
    'Costa Rica',
    'Croatia',
    'Cuba',
    'Cyprus',
    'Czechia',
    'Democratic Republic of the Congo',
    'Denmark',
    'Djibouti',
    'Dominica',
    'Dominican Republic',
    'Ecuador',
    'Egypt',
    'El Salvador',
    'Equatorial Guinea',
    'Eritrea',
    'Estonia',
    'Eswatini',
    'Ethiopia',
    'Fiji',
    'Finland',
    'France',
    'Gabon',
    'Gambia',
    'Georgia',
    'Germany',
    'Ghana',
    'Greece',
    'Grenada',
    'Guatemala',
    'Guinea',
    'Guinea-Bissau',
    'Guyana',
    'Haiti',
    'Honduras',
    'Hong Kong',
    'Hungary',
    'Iceland',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Ivory Coast',
    'Jamaica',
    'Japan',
    'Jordan',
    'Kazakhstan',
    'Kenya',
    'Kiribati',
    'Kosovo',
    'Kuwait',
    'Kyrgyzstan',
    'Laos',
    'Latvia',
    'Lebanon',
    'Lesotho',
    'Liberia',
    'Libya',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Macau',
    'Madagascar',
    'Malawi',
    'Malaysia',
    'Maldives',
    'Mali',
    'Malta',
    'Marshall Islands',
    'Mauritania',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Moldova',
    'Monaco',
    'Mongolia',
    'Montenegro',
    'Morocco',
    'Mozambique',
    'Myanmar',
    'Namibia',
    'Nauru',
    'Nepal',
    'Netherlands',
    'New Zealand',
    'Nicaragua',
    'Niger',
    'Nigeria',
    'North Korea',
    'North Macedonia',
    'Norway',
    'Oman',
    'Pakistan',
    'Palau',
    'Palestine',
    'Panama',
    'Papua New Guinea',
    'Paraguay',
    'Peru',
    'Philippines',
    'Poland',
    'Portugal',
    'Puerto Rico',
    'Qatar',
    'Romania',
    'Russia',
    'Rwanda',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Vincent and the Grenadines',
    'Samoa',
    'San Marino',
    'Sao Tome and Principe',
    'Saudi Arabia',
    'Senegal',
    'Serbia',
    'Seychelles',
    'Sierra Leone',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Solomon Islands',
    'Somalia',
    'South Africa',
    'South Korea',
    'South Sudan',
    'Spain',
    'Sri Lanka',
    'Sudan',
    'Suriname',
    'Sweden',
    'Switzerland',
    'Syria',
    'Taiwan',
    'Tajikistan',
    'Tanzania',
    'Thailand',
    'Timor-Leste',
    'Togo',
    'Tonga',
    'Trinidad and Tobago',
    'Tunisia',
    'Turkey',
    'Turkmenistan',
    'Tuvalu',
    'Uganda',
    'Ukraine',
    'United Arab Emirates',
    'United Kingdom',
    'Uruguay',
    'Uzbekistan',
    'Vanuatu',
    'Vatican City',
    'Venezuela',
    'Vietnam',
    'Yemen',
    'Zambia',
    'Zimbabwe',
];
export const addressInputDescriptor = {
    type: 'address-input',
    surfaces: ['rental-agreement', 'checkin-form'],
    blockLabel: 'Address Input',
    defaultLabel: 'Enter your address',
    requiredDefault: true,
    extraAttrs: [],
    valueChannel: 'progress',
    editorPlaceholderHtml: (label) => `<div style="display:flex;align-items:center;gap:10px;pointer-events:none;">
      <span style="font-size:20px;">&#128205;</span>
      <div>
        <div style="font-weight:600;font-size:14px;color:#334155;">${escapeHtml(label)}</div>
        <div style="font-size:12px;color:#94a3b8;">Guest enters their address (street, city, zip, country)</div>
      </div>
    </div>`,
    validate: (_field, value) => {
        const address = parseAddressValue(value);
        if (!address || isAddressEmpty(address))
            return null;
        return missingRequiredAddressSubfields(address).length === 0 ? null : 'Please complete all required address fields';
    },
    isComplete: (field, value) => {
        const address = parseAddressValue(value);
        if (!address || isAddressEmpty(address))
            return !field.required;
        return missingRequiredAddressSubfields(address).length === 0;
    },
    renderPdfHtml: (field, value) => {
        const address = parseAddressValue(value);
        const lines = [];
        if (address) {
            if (address.line1)
                lines.push(escapeHtml(address.line1));
            if (address.line2)
                lines.push(escapeHtml(address.line2));
            const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.zip].filter(Boolean).join(' ');
            if (cityLine)
                lines.push(escapeHtml(cityLine));
            if (address.country)
                lines.push(escapeHtml(address.country));
        }
        return renderLabeledValuePdfHtml(pdfLabelFor(field), lines.join('<br/>'));
    },
};
